import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { FormControl, FormLabel } from "@mui/material";
import { Box } from "@mui/system";

import CloudPlatformChip from "@features/common/CloudPlatformChip";
import { useAppDispatch } from "@features/common/StoreHook";
import AddDuplicateDeleteButtonGroup from "../../common/ButtonGroups/AddDuplicateDeleteButtonGroup";
import ExportExcel from "@features/common/ExportExcel";
import SortTable, { IProperty } from "@features/common/Table/SortTable";
import { getSelectedCustomerId } from "@features/customers/customerSlice";
import { fetchVMs, getAllVMs, selectVMStatus } from "../environmentsSlice";
import { IVirtualMachine } from "../types";
import { formControlStyle, formLabelStyle } from "../style";
import ResourceContainer from "./ResourceContainer";
import AddCompute from "./AddCompute";

/**
 * The view that shows customer's VMs.
 *
 * TODO:
 *  - Many of the buttons are placeholders at the moment and have been disabled until they can actually be used.
 * @returns
 */

// Interface for VM resources that are displayed in the table,
// different from IVirtualMachine only because the cloud_platform property
// is a JSX element so that it can be displayed as a colored chip.
interface VMResource extends Omit<IVirtualMachine, "cloud_platform"> {
  cloudPlatform?: JSX.Element | null;
}

const Compute = () => {
  const [checked, setChecked] = useState<string | undefined>();
  const [showAddDialog, setShowAddDialog] = useState<boolean>(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const customerId = useSelector(getSelectedCustomerId);
  const vmResources = useSelector(getAllVMs);
  const isLoading = useSelector(selectVMStatus);

  useEffect(() => {
    if (customerId) {
      dispatch(fetchVMs(customerId));
    }
  }, [customerId]);

  const properties: IProperty<VMResource>[] = [
    { key: "name", label: t("label-machine-name") },
    { key: "cloudPlatform", label: t("label-cloud-platform") },
    { key: "type", label: t("label-type") },
    { key: "category", label: t("label-category") },
    { key: "level", label: t("label-level") },
    { key: "status", label: t("label-status") },
    { key: "enable_shutdown", label: t("label-shutdown-enabled") },
  ];

  const handleChange = (id: string) => {
    setChecked(id);
  };

  const getSelectedVM = () =>
    vmResources.find((v: IVirtualMachine) => v.name === checked);

  const vmResourcesArr = vmResources.map((vm: IVirtualMachine) => ({
    ...vm,
    cloudPlatform: (
      <CloudPlatformChip cloudPlatform={vm.cloud_platform ?? "N/A"} />
    ),
  }));

  return (
    <Box>
      <FormControl component="fieldset" sx={formControlStyle}>
        <FormLabel component="legend" sx={formLabelStyle}>
          {t("label-environments")}
        </FormLabel>
        {!isLoading && (
          <Box sx={{ textAlign: "right" }}>
            <ExportExcel
              data={vmResources}
              fileName="virtual_machines"
              coloredButton={true}
            />
          </Box>
        )}
        <SortTable
          properties={properties}
          idProperty={"name"}
          handleChange={handleChange}
          isLoading={isLoading === "loading"}
          selectedId={checked}
          data={vmResourcesArr}
          key={vmResources.length}
        />
        <AddDuplicateDeleteButtonGroup
          disabled={true}
          handleAdd={() => setShowAddDialog(true)}
        />
      </FormControl>
      <AddCompute
        show={showAddDialog}
        onCancel={() => setShowAddDialog(false)}
      />
      <ResourceContainer key={checked} resource={getSelectedVM()} />
    </Box>
  );
};

export default Compute;
